/**
 * `useEditorSnapshot` — the frame-coalesced, value-gated selection signal.
 *
 * Replaces `<EditorToolbarMount>`'s raw
 * `selectionchange → setSelTick(n+1) → re-read in the render body` tick, which
 * re-rendered the whole 92-command ribbon once per input event (measured: 63% of
 * all main-thread work during a drag — see `editorSnapshot.ts`).
 *
 * TWO gates, in order:
 *
 *   1. FRAME COALESCING. `selectionchange` fires once per input event — one per
 *      `pointermove` on the canvas, i.e. up to the pointer's report rate, which
 *      on modern hardware is several per frame. A `requestAnimationFrame` latch
 *      collapses every event within one frame into ONE sample. The toolbar only
 *      has to be correct when the user can SEE it, which is once per frame.
 *
 *   2. VALUE GATING. The sample runs OUTSIDE React (in the rAF callback) and only
 *      calls `setState` when the snapshot is structurally different from the one
 *      already committed. No `setState` → React does not re-render at all, so the
 *      ribbon subtree is never reconciled for a selection that changed nothing the
 *      toolbar shows. Dragging through plain text is exactly that case.
 *
 * NOT a stale closure: the sampler reads the LIVE handle and host through refs
 * that are refreshed on every render, so its dependency array is honestly empty
 * — nothing it closes over is ever read stale. (Emptying a dep array over values
 * read DIRECTLY would be the stale-closure bug; this is the opposite fix.)
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { EditorHostBridge } from "@norkron/web-elaborate-editor";
import {
  type EditorSnapshot,
  editorSnapshotEql,
  readEditorSnapshot,
  type SnapshotSource,
} from "./editorSnapshot";

/** Observability seam for the perf pin: how many times the signal SAMPLED the
 *  handle, and how many of those samples actually committed a re-render. */
export interface SnapshotStats {
  /** rAF callbacks that ran (≤ 1 per animation frame). */
  samples: number;
  /** Samples whose value differed → a React state commit. */
  commits: number;
  /** `selectionchange` events observed (coalesced into `samples`). */
  events: number;
}

const raf = (cb: () => void): number =>
  typeof requestAnimationFrame === "function"
    ? requestAnimationFrame(() => cb())
    : (setTimeout(cb, 16) as unknown as number);

const cancelRaf = (id: number): void => {
  if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(id);
  else clearTimeout(id as unknown as ReturnType<typeof setTimeout>);
};

export function useEditorSnapshot(
  handle: SnapshotSource,
  host: Partial<EditorHostBridge>,
  revision?: number,
  stats?: SnapshotStats,
): EditorSnapshot {
  const [snap, setSnap] = useState<EditorSnapshot>(() =>
    readEditorSnapshot(handle, host),
  );

  // Live refs — refreshed every render, so the sampler below never reads a stale
  // handle/host even though its dep list is empty.
  const handleRef = useRef(handle);
  handleRef.current = handle;
  const hostRef = useRef(host);
  hostRef.current = host;
  const snapRef = useRef(snap);
  snapRef.current = snap;
  const statsRef = useRef(stats);
  statsRef.current = stats;
  const frameRef = useRef<number | null>(null);

  const sample = useCallback(() => {
    frameRef.current = null;
    const s = statsRef.current;
    if (s) s.samples += 1;
    const next = readEditorSnapshot(handleRef.current, hostRef.current);
    // GATE 2 — nothing the toolbar renders differs, so do NOT commit.
    if (editorSnapshotEql(snapRef.current, next)) return;
    if (s) s.commits += 1;
    snapRef.current = next;
    setSnap(next);
  }, []);

  const schedule = useCallback(() => {
    // GATE 1 — at most one sample per animation frame.
    if (frameRef.current != null) return;
    frameRef.current = raf(sample);
  }, [sample]);

  useEffect(() => {
    const onSel = (): void => {
      const s = statsRef.current;
      if (s) s.events += 1;
      schedule();
    };
    document.addEventListener("selectionchange", onSel);
    return () => {
      document.removeEventListener("selectionchange", onSel);
      if (frameRef.current != null) cancelRaf(frameRef.current);
      frameRef.current = null;
    };
  }, [schedule]);

  // An EDIT moves undo/redo depth and the painter arm WITHOUT moving the caret,
  // and swapping the surface swaps the handle — both must resample. The value
  // gate still decides whether that sample commits anything.
  useEffect(() => {
    schedule();
  }, [revision, handle, host, schedule]);

  return snap;
}
